import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Play, ArrowLeft, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

const WHATSAPP_GROUP_URL = "https://chat.whatsapp.com/BEfuQsZUCZQ3VIO1uGJWJG";

const ThankYouPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Track conversion event (non-blocking)
    fetch('/api/analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'conversion',
        page: '/thank-you',
        metadata: { conversion_type: 'signup_completed' }
      })
    }).catch(() => {});
  }, []);

  const featuredEpisodes = [
    {
      id: 'Czt7NZzACbc',
      title: 'Deported, Broke, Alone',
      subtitle: 'How Jamaica Became My Everything'
    },
    {
      id: 'rrI2rS_n_2Q',
      title: 'They Told Me to Leave',
      subtitle: 'But I Stayed in Jamaica'
    },
    {
      id: 'Hj8hkFxHlEE',
      title: "Jamaica's Unlikely Hero",
      subtitle: 'Six-Foot-Six, Blonde & British'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Simple Header */}
      <header className="border-b border-border bg-background/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')}
            className="hover:opacity-80 transition-opacity"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_explore-elsewhere/artifacts/3xxh7ml4_Podcast%20Logo%20Babes%2C%20How%20Did%20You%20Get%20Here%20%284%29.png"
              alt="Babes, How Did You Get Here?"
              className="h-10 w-auto"
            />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: 'spring' }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6"
          >
            <CheckCircle2 className="w-10 h-10 text-primary" />
          </motion.div>

          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            You're In! 🎉
          </h1>
          <p className="text-lg text-muted-foreground max-w-[60ch] mx-auto leading-relaxed">
            Welcome to the A Life Elsewhere community. We're excited to have you join us on this journey.
          </p>
        </motion.div>

        {/* Next Steps Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card className="p-8 mb-8">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-primary mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-2">What Happens Next?</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Here's what to expect as you join our global community:
                </p>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Check Your Email 📬</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    You should receive a welcome email from us shortly. If you don't see it, check your spam folder.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#25D366] font-bold">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">Join the WhatsApp Group 📱</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    Join our exclusive community WhatsApp group where the real conversations happen! Connect with dreamers, nomads, and storytellers worldwide.
                  </p>
                  <Button
                    onClick={() => window.open(WHATSAPP_GROUP_URL, '_blank')}
                    className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold rounded-xl"
                    data-testid="join-whatsapp-button"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Join WhatsApp Group Now
                  </Button>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Catch Up on Episodes 🎧</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    New episodes drop every Monday & Thursday. Subscribe on YouTube so you never miss a story.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
        >
          <Button 
            size="lg"
            className="rounded-xl"
            onClick={() => window.open('https://www.youtube.com/@ALifeElsewhere?sub_confirmation=1', '_blank')}
          >
            <Play className="w-5 h-5 mr-2" />
            Subscribe on YouTube
          </Button>
          <Button 
            size="lg"
            variant="outline"
            className="rounded-xl"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Button>
        </motion.div>

        {/* Featured Episodes to Watch */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-2xl font-bold text-center mb-6">Start With These Episodes</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {featuredEpisodes.map((episode) => (
              <Card 
                key={episode.id}
                className="overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => window.open(`https://www.youtube.com/watch?v=${episode.id}`, '_blank')}
              >
                <div className="relative aspect-video overflow-hidden">
                  <img 
                    src={`https://img.youtube.com/vi/${episode.id}/mqdefault.jpg`}
                    alt={episode.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                      <Play className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-sm mb-1">{episode.title}</h3>
                  <p className="text-xs text-muted-foreground">{episode.subtitle}</p>
                </div>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12 pt-8 border-t border-border"
        >
          <p className="text-sm text-muted-foreground mb-4">
            Follow us on social media for behind-the-scenes content and updates
          </p>
          <div className="flex justify-center gap-6">
            <a href="https://www.instagram.com/a_life_elsewhere" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm font-medium">
              Instagram
            </a>
            <a href="https://www.tiktok.com/@April_Jackson_Show" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm font-medium">
              TikTok
            </a>
            <a href="https://www.youtube.com/@ALifeElsewhere" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm font-medium">
              YouTube
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ThankYouPage;
